import { adminFetch } from "./_client";

// Admin client for the Ops "Host activity check" campaign.
// All calls go through adminFetch (fresh token, step-up, retries).

export type ActivityBand = "recent" | "one_to_two" | "older" | "unknown";
export const ACTIVITY_BANDS: ActivityBand[] = ["recent", "one_to_two", "older", "unknown"];

export interface HostActivityTotals {
  activeHosts?: number;
  inactiveHosts?: number;
  activeCars?: number;
  inactiveCars?: number;
}

export interface HostActivityCountry {
  country: string;
  eligibleHosts?: number;
  cars?: number;
  invalidEmailHosts?: number;
  language?: string | null;
  bands?: Partial<Record<ActivityBand, number>>;
  /** Hosts excluded from the audience, keyed by reason. */
  excluded?: Record<string, number>;
}

export interface HostActivityAudience {
  countries?: HostActivityCountry[];
  totals?: HostActivityTotals;
  blockers?: string[];
  reviews?: { locale: string; reviewed: boolean }[];
}

export interface HostActivityPreview {
  locale?: string;
  hostName?: string;
  fleetCount?: number;
  subject?: string;
  emailHtml?: string;
  pages?: { confirm?: string; pause?: string; sold?: string };
  blockers?: string[];
}

export interface HostActivityBatchPlan {
  id: string;
  count: number;
  cars: number;
  countries: string[];
  blockers?: string[];
}

export interface HostActivitySendResult {
  status?: string;
  processed?: number;
  sent?: number;
  skipped?: number;
  failed?: number;
  ambiguous?: number;
  pending?: number;
}

export interface HostActivityBatch {
  id: string;
  createdAt?: string | null;
  sentAt?: string | null;
  countries?: string[];
  count?: number;
  status?: string;
  reminderEligible?: boolean;
}

export interface HostActivityWeekly {
  week: string;
  cohort?: string;
  /** e.g. pre-send baseline vs post-send. */
  period?: string | null;
  requests?: number;
  declined?: number;
  unanswered?: number;
  pending?: number;
  unknown?: number;
  declinedShare?: number | null;
  unansweredShare?: number | null;
  evidence?: string | null;
}

export interface HostActivityRecipientIssue {
  id: string | number;
  reason?: string | null;
  state?: string | null;
  hosts?: number;
  cars?: number;
}

export interface HostActivityResults {
  batches?: HostActivityBatch[];
  outcomes?: { outcome: string; hosts?: number; cars?: number }[];
  weekly?: HostActivityWeekly[];
  totals?: HostActivityTotals;
  processing?: {
    pending?: number; failed?: number; ambiguous?: number;
    maintenancePending?: number; maintenanceFailed?: number; maintenanceAmbiguous?: number;
  };
  /** Aggregated recipient issues; no emails or names. */
  recipientIssues?: HostActivityRecipientIssue[];
  providerRates?: { openRate?: number | null; clickRate?: number | null; bounceRate?: number | null };
  blockers?: string[];
}

const json = (body: unknown): RequestInit => ({
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

export function fetchHostActivityAudience(token: string) {
  return adminFetch<HostActivityAudience>("/admin/host-activity/audience", token);
}

export function fetchHostActivityPreview(token: string, country: string, reminder: boolean) {
  const qs = new URLSearchParams({ country, reminder: String(reminder) });
  return adminFetch<HostActivityPreview>(`/admin/host-activity/preview?${qs}`, token);
}

export function sendHostActivityTest(token: string, country: string) {
  return adminFetch<Record<string, unknown>>("/admin/host-activity/test", token, json({ country }));
}

export function planHostActivityBatch(
  token: string,
  body: { countries: string[]; band?: ActivityBand; reminderOf?: string },
) {
  return adminFetch<HostActivityBatchPlan>("/admin/host-activity/batches", token, json(body));
}

export function sendHostActivityBatch(token: string, id: string, expectedCount: number) {
  return adminFetch<HostActivitySendResult>(
    `/admin/host-activity/batches/${encodeURIComponent(id)}/send`,
    token,
    json({ expectedCount }),
  );
}

export function fetchHostActivityResults(token: string, filters: { country?: string; batch?: string }) {
  const qs = new URLSearchParams();
  if (filters.country) qs.set("country", filters.country);
  if (filters.batch) qs.set("batch", filters.batch);
  const s = qs.toString();
  return adminFetch<HostActivityResults>(`/admin/host-activity/results${s ? `?${s}` : ""}`, token);
}
